import { useCallback, useEffect, useMemo, useState } from 'react';

import { invalidateDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import {
  editTemplateFavoriteStatus,
  getRecommendedTemplates,
} from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { useQuery } from '@tanstack/react-query';

const ONBOARDING_DOCUMENT_TEMPLATES_KEY = 'OnboardingDocumentTemplates';

interface UseDocumentSelectionParams {
  specialtyId?: string;
  language?: string;
  country?: string;
}

export function useDocumentSelection({
  specialtyId,
  language,
  country,
}: UseDocumentSelectionParams = {}) {
  const effectiveSpecialtyId = specialtyId === 'other' ? undefined : specialtyId;

  const {
    data: templates,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      ONBOARDING_DOCUMENT_TEMPLATES_KEY,
      effectiveSpecialtyId,
      language,
      country,
    ],
    queryFn: async () => {
      const { data } = await getRecommendedTemplates({
        specialtyId: effectiveSpecialtyId,
        language,
        country,
      });
      return data.templates;
    },
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [effectiveSpecialtyId, language, country]);

  const visibleTemplates = useMemo(() => templates ?? [], [templates]);

  const hasError = isError || (!isLoading && !templates);

  const selectedTemplates = useMemo(() => {
    return visibleTemplates.filter((t) => selectedIds.has(t.id));
  }, [visibleTemplates, selectedIds]);

  const toggleSelection = useCallback((templateId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  }, []);

  const submitFavorites = useCallback(
    async (additionalFavoriteIds: string[] = []) => {
      const idSet = new Set<string>([...selectedIds, ...additionalFavoriteIds]);
      if (idSet.size === 0) {
        return;
      }

      const results = await Promise.allSettled(
        Array.from(idSet).map((id) => editTemplateFavoriteStatus(id, true))
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        logger.error('Failed to favorite some templates', {
          failed: failures.length,
          total: idSet.size,
        });
      }

      await Promise.all([
        invalidateDocumentTemplates(),
        reactQueryClient.invalidateQueries({ queryKey: ['Get_templates'] }),
      ]);
    },
    [selectedIds]
  );

  return {
    templates: visibleTemplates,
    selectedIds,
    selectedTemplates,
    isLoading,
    hasError,
    toggleSelection,
    submitFavorites,
  };
}

export type UseDocumentSelectionReturn = ReturnType<typeof useDocumentSelection>;
